import type { WeatherReport } from '../../shared/contract'

/**
 * Data and asset credits as real links (licence compliance). This compact form is what the
 * footer and the map preview show; the Sources sheet adds full names, licence deeds and
 * required notices (sheets/creditList.tsx).
 */
export interface Credit {
  id: CreditId
  /** Footer form. */
  short: string
  href: string
  /** Licence name where the licence asks to be named (CC BY, MIT, ODbL). */
  license?: string
}

export type CreditId =
  | 'nws'
  | 'openmeteo'
  | 'cams'
  | 'era5'
  | 'metno'
  | 'airnow'
  | 'epauv'
  | 'iem'
  | 'spc'
  | 'nhc'
  | 'coops'
  | 'ncei'
  | 'cpc'
  | 'osm'
  | 'openmaptiles'
  | 'openfreemap'
  | 'gibs'
  | 'naturalearth'
  | 'geonames'
  | 'meteocons'

const c = (id: CreditId, short: string, href: string, license?: string): Credit => ({ id, short, href, license })

export const CREDITS: Record<CreditId, Credit> = {
  nws: c('nws', 'NOAA / NWS', 'https://www.weather.gov'),
  openmeteo: c('openmeteo', 'Weather data by Open-Meteo.com', 'https://open-meteo.com', 'CC BY 4.0'),
  cams: c('cams', 'Copernicus CAMS', 'https://atmosphere.copernicus.eu'),
  era5: c('era5', 'Copernicus ERA5', 'https://climate.copernicus.eu'),
  metno: c('metno', 'MET Norway', 'https://api.met.no', 'CC BY 4.0'),
  airnow: c('airnow', 'EPA AirNow', 'https://www.airnow.gov'),
  epauv: c('epauv', 'EPA UV Index', 'https://www.epa.gov/sunsafety/uv-index-1'),
  iem: c('iem', 'Iowa Environmental Mesonet', 'https://mesonet.agron.iastate.edu'),
  spc: c('spc', 'NOAA SPC', 'https://www.spc.noaa.gov'),
  nhc: c('nhc', 'NOAA NHC', 'https://www.nhc.noaa.gov'),
  coops: c('coops', 'NOAA CO-OPS', 'https://tidesandcurrents.noaa.gov'),
  ncei: c('ncei', 'NOAA NCEI', 'https://www.ncei.noaa.gov/products/land-based-station/global-historical-climatology-network-daily'),
  cpc: c('cpc', 'NOAA CPC', 'https://www.cpc.ncep.noaa.gov'),
  osm: c('osm', '© OpenStreetMap', 'https://www.openstreetmap.org/copyright', 'ODbL'),
  openmaptiles: c('openmaptiles', '© OpenMapTiles', 'https://www.openmaptiles.org'),
  openfreemap: c('openfreemap', 'OpenFreeMap', 'https://openfreemap.org'),
  gibs: c('gibs', 'NASA GIBS', 'https://nasa-gibs.github.io/gibs-api-docs/'),
  naturalearth: c('naturalearth', 'Natural Earth', 'https://www.naturalearthdata.com', 'Public domain'),
  geonames: c('geonames', 'GeoNames', 'https://www.geonames.org', 'CC BY 4.0'),
  meteocons: c('meteocons', 'Meteocons', 'https://github.com/basmilius/weather-icons', 'MIT'),
}

/** Credits for the sources this report actually used (status ok or partial), in reading order. */
export function reportCredits(report: WeatherReport): Credit[] {
  const used = new Set(report.sources.filter((s) => s.status === 'ok' || s.status === 'partial').map((s) => s.id))
  const ids: CreditId[] = []
  if (used.has('nws_forecast') || used.has('nws_observations') || used.has('nws_alerts')) ids.push('nws')
  if ([...used].some((id) => id.startsWith('openmeteo_') || id === 'normals')) ids.push('openmeteo')
  if (used.has('metno')) ids.push('metno')
  if (used.has('openmeteo_air')) ids.push('cams')
  if (used.has('normals')) ids.push('era5')
  if (used.has('airnow')) ids.push('airnow')
  if (used.has('epa_uv')) ids.push('epauv')
  if (used.has('iem_radar')) ids.push('iem')
  if (used.has('spc')) ids.push('spc')
  if (used.has('nhc')) ids.push('nhc')
  if (used.has('coops')) ids.push('coops')
  return ids.map((id) => CREDITS[id])
}
